import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface EditTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticket: { subject: string; description: string | null };
  onSave: (data: { subject: string; description: string }) => Promise<void>;
  updating: boolean;
}

export function EditTicketDialog({ open, onOpenChange, ticket, onSave, updating }: EditTicketDialogProps) {
  const { t } = useTranslation(['tickets', 'common']);
  const [subject, setSubject] = useState(ticket.subject);
  const [description, setDescription] = useState(ticket.description ?? '');

  // Reset form when dialog opens with fresh ticket data
  useEffect(() => {
    if (open) {
      setSubject(ticket.subject);
      setDescription(ticket.description ?? '');
    }
  }, [open, ticket.subject, ticket.description]);

  const hasChanges = subject !== ticket.subject || description !== (ticket.description ?? '');
  const canSave = subject.trim().length > 0 && hasChanges && !updating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t('tickets:edit.title')}</DialogTitle>
          <DialogDescription>{t('tickets:edit.subtitle')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-subject">
              {t('tickets:edit.subject')} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={255}
              placeholder={t('tickets:edit.subjectPlaceholder')}
              disabled={updating}
            />
            <p className="text-xs text-slate-400 text-right">{subject.length}/255</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">{t('tickets:edit.description')}</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={10000}
              placeholder={t('tickets:edit.descriptionPlaceholder')}
              rows={5}
              disabled={updating}
            />
            <p className="text-xs text-slate-400 text-right">{description.length}/10000</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updating}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={() => onSave({ subject: subject.trim(), description })} disabled={!canSave}>
            {updating && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {updating ? t('tickets:edit.saving') : t('tickets:edit.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
